import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Plus, Trash2, X } from 'lucide-react'; // ✅ Changed from Save to ArrowRight
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ProjectAmenitiesForm = ({ amenitiesData, setAmenitiesData, onSave, setActiveTab, isProject }) => {
  const cardCls = 'bg-white rounded-xl shadow p-8 mb-10';
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1';
  const inputCls =
    'w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none';
  const textareaCls =
    'w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none resize-none';

  const skipAutosave = useRef(false);
  const [draftId, setDraftId] = useState(null);
  const [newAmenity, setNewAmenity] = useState({ name: '', description: '' });

  let saveTimer = useRef(null);
  const isInitialLoad = useRef(true);

  // ✅ Load existing draft on mount
  useEffect(() => {
    const loadDraft = async () => {
      const storedDraftId = localStorage.getItem('draftId');
      if (!storedDraftId) {
        isInitialLoad.current = false;
        return;
      }

      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(`${API_BASE_URL}/admin/get-draft-project/${storedDraftId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data?.draft) {
          setAmenitiesData(res.data.draft.draft_data);
          setDraftId(res.data.draft.draft_id);
        }
      } catch (err) {
        console.error('Error loading draft:', err);
      } finally {
        isInitialLoad.current = false;
      }
    };

    if (!isProject) loadDraft();
    else isInitialLoad.current = false;
  }, [isProject, setAmenitiesData]);

  // ✅ Autosave draft
  const autosaveDraft = async () => {
    if (!amenitiesData.project_name || amenitiesData.project_name.trim() === '') return;

    const token = localStorage.getItem('token');
    const payload = {
      draft_id: draftId,
      formData: amenitiesData,
    };

    const res = await axios.post(`${API_BASE_URL}/admin/save-draft-project`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!draftId && res.data.draft_id) {
      setDraftId(res.data.draft_id);
      localStorage.setItem('draftId', res.data.draft_id);
    }
  };

  useEffect(() => {
    if (!amenitiesData || !amenitiesData.project_name?.trim()) return;

    if (skipAutosave.current) {
      skipAutosave.current = false;
      return;
    }

    if (saveTimer.current) clearTimeout(saveTimer.current);

    saveTimer.current = setTimeout(() => {
      autosaveDraft();
    }, 800);

    return () => clearTimeout(saveTimer.current);
  }, [amenitiesData]);

  const amenities = amenitiesData?.project_amenities || [];

  // ✅ Add new amenity
  const handleAddAmenity = () => {
    setAmenitiesData((prev) => {
      const base = prev || {};
      const updatedAmenities = Array.isArray(base.project_amenities)
        ? [...base.project_amenities, newAmenity]
        : [newAmenity];

      return { ...base, project_amenities: updatedAmenities };
    });

    setNewAmenity({ name: '', description: '' });
  };

  // ✅ Update existing amenity
  const handleAmenityChange = (index, field, value) => {
    setAmenitiesData((prev) => {
      const updatedAmenities = prev.project_amenities ? [...prev.project_amenities] : [];
      updatedAmenities[index] = { ...updatedAmenities[index], [field]: value };
      return { ...prev, project_amenities: updatedAmenities };
    });
  };

  // ✅ Remove amenity
  const handleRemoveAmenity = (index) => {
    setAmenitiesData((prev) => {
      const updatedAmenities = prev.project_amenities
        ? prev.project_amenities.filter((_, i) => i !== index)
        : [];
      return { ...prev, project_amenities: updatedAmenities };
    });
  };

  const clearForm = () => {
    skipAutosave.current = true;
    setAmenitiesData({ ...amenitiesData, project_amenities: [] });
    localStorage.removeItem('draftId');
  };

  return (
    <section className={cardCls}>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-blue-700">Project Amenities</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={clearForm}
            className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
          >
            <X size={18} /> Clear
          </button>
          {/* ✅ Changed Save to Next button */}
          <button
            onClick={() => {
              setActiveTab('approval'); // ✅ Navigate to approval tab instead of home
            }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg"
          >
            Next <ArrowRight size={18} /> {/* ✅ Changed icon and text */}
          </button>
        </div>
      </div>

      {/* Add New Amenity */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 mb-8 bg-gray-50">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Add New Amenity</h3>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className={labelCls}>Amenity Name</label>
            <input
              type="text"
              value={newAmenity.name}
              onChange={(e) => setNewAmenity({ ...newAmenity, name: e.target.value })}
              className={inputCls}
              placeholder="e.g., Swimming Pool"
            />
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <input
              type="text"
              value={newAmenity.description}
              onChange={(e) => setNewAmenity({ ...newAmenity, description: e.target.value })}
              className={inputCls}
              placeholder="e.g., Spacious pool for residents"
            />
          </div>
        </div>
        <button
          onClick={handleAddAmenity}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
        >
          <Plus size={18} /> Add Amenity
        </button>
      </div>

      {/* Amenities List */}
      <div className="space-y-6">
        {amenities.length === 0 ? (
          <p className="text-gray-500 text-sm">No amenities added yet.</p>
        ) : (
          amenities.map((amenity, index) => (
            <div
              key={index}
              className="border rounded-lg p-6 bg-white shadow-sm hover:shadow-md transition"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-800 text-lg">
                  Amenity {index + 1}
                </h3>
                <button
                  onClick={() => handleRemoveAmenity(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 size={18} />
                </button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Name</label>
                  <input
                    type="text"
                    value={amenity.name}
                    onChange={(e) => handleAmenityChange(index, 'name', e.target.value)}
                    className={inputCls}
                    placeholder="e.g., Park"
                  />
                </div>
                <div>
                  <label className={labelCls}>Description</label>
                  <textarea
                    value={amenity.description}
                    onChange={(e) =>
                      handleAmenityChange(index, 'description', e.target.value)
                    }
                    className={textareaCls}
                    rows={2}
                    placeholder="Describe this amenity"
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
};

export default ProjectAmenitiesForm;